import type { Modification } from '@carboncopyco/types';
import type { NavigationEditSchemaType } from '@carboncopyco/types';
import { TextCaption } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { RulesManager, type RulesManagerProps } from '../RulesManager';
import { AddNavigationRuleButton } from './AddNavigationRuleButton';
import {
  BlockInteractionsSettings,
  type BlockInteractionsSettingsProps,
} from './BlockInteractionsSettings';
import { NavigationRulesContent } from './NavigationRulesContent';
import { NavigationRulesContext } from './NavigationRulesManagerContext';

export interface NavigationManagerProps
  extends Pick<RulesManagerProps, 'isOpen' | 'onClose' | 'onLearnMoreClick'> {
  navigationModifications: Modification<NavigationEditSchemaType.blockInteractions>[];
  createNavigationModification?: (
    blockInteractionsRule: Modification<NavigationEditSchemaType.blockInteractions>
  ) => Promise<unknown>;
  editNavigationModification?: (
    navigationModification: Modification<NavigationEditSchemaType.blockInteractions>
  ) => Promise<unknown>;
  deleteNavigationModification?: (
    navigationModification: Modification<NavigationEditSchemaType.blockInteractions>
  ) => Promise<unknown>;
  reorderNavigationModification?: (
    moved: Modification<NavigationEditSchemaType.blockInteractions>,
    after?: Modification<NavigationEditSchemaType.blockInteractions>
  ) => Promise<unknown>;
  startBlockInteractionsMode: () => void;
  createBlockInteractionsSettings?: BlockInteractionsSettingsProps['createBlockInteractionsSettings'];
  blockInteractionsSettings?: BlockInteractionsSettingsProps['blockInteractionsSettings'];
}

export const NavigationManager = ({
  isOpen,
  onClose,
  navigationModifications,
  deleteNavigationModification,
  editNavigationModification,
  startBlockInteractionsMode,
  reorderNavigationModification,
  createBlockInteractionsSettings,
  blockInteractionsSettings,
}: NavigationManagerProps) => {
  const [isEditScreenVisible, { set: setIsEditScreenVisible }] =
    useBoolean(false);
  const { editorBlockInteractionsSettings } = useFlags();
  const isEmpty = !navigationModifications?.length;
  const contextValue = useMemo(
    () => ({
      rules: navigationModifications,
    }),
    [navigationModifications]
  );
  return (
    <NavigationRulesContext.Provider value={contextValue}>
      <RulesManager
        isOpen={isOpen}
        onClose={onClose}
      >
        <RulesManager.Header>
          <RulesManager.Header.Title label="Block Events" />
          <RulesManager.Header.Action>
            <AddNavigationRuleButton onSelect={startBlockInteractionsMode} />
          </RulesManager.Header.Action>
        </RulesManager.Header>

        {isEmpty && editorBlockInteractionsSettings && (
          <div className="flex mt-5 border-b-ds-natural-150  border-b">
            <BlockInteractionsSettings
              isEditScreenVisible={isEditScreenVisible}
              setIsEditScreenVisible={setIsEditScreenVisible}
              blockInteractionsSettings={blockInteractionsSettings}
              createBlockInteractionsSettings={createBlockInteractionsSettings}
            />
          </div>
        )}

        {isEmpty ? (
          !isEditScreenVisible && (
            <RulesManager.EmptyState color="blue">
              <RulesManager.EmptyState.Title label="Manage demo navigation" />
              <span>
                <TextCaption
                  label="Globally prevent clicks on links or buttons."
                  color="text-ds-natural-600"
                />
              </span>
            </RulesManager.EmptyState>
          )
        ) : (
          <>
            <div className="flex flex-col mt-2 mb-5 gap-5">
              <TextCaption
                label="Globally prevent clicks on links or buttons."
                color="text-ds-natural-600"
              />
              {editorBlockInteractionsSettings && (
                <BlockInteractionsSettings
                  isEditScreenVisible={isEditScreenVisible}
                  setIsEditScreenVisible={setIsEditScreenVisible}
                  blockInteractionsSettings={blockInteractionsSettings}
                  createBlockInteractionsSettings={
                    createBlockInteractionsSettings
                  }
                />
              )}
            </div>
            <NavigationRulesContent
              deleteNavigationModification={deleteNavigationModification}
              reorderNavigationModification={reorderNavigationModification}
              editNavigationModification={editNavigationModification}
            />
          </>
        )}
      </RulesManager>
    </NavigationRulesContext.Provider>
  );
};
